'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useState } from 'react';
export const MORFI_HERO_PRODUCT_CTA_SELECTOR = '[data-morfi-hero-product-cta]' as const;

/** Cap the streaming-swap retry so a page without a hero CTA can never spin. */
const HERO_CTA_RESOLVE_MAX_FRAMES = 120;

/**
 * Resolve the on-screen hero CTA. React's streaming SSR can leave a duplicate
 * copy of the hero inside a `<div hidden id="S:…">` template while it hydrates,
 * so a plain `querySelector` (first match in document order) sometimes returns
 * the `display:none` clone. Observing that clone never fires, so the header
 * reveal silently dies on desktop (mobile is unaffected because it uses the
 * separate scroll-driven floating CTA). A `display:none` node reports zero
 * client rects, so prefer the copy that is actually laid out.
 */
function resolveLaidOutHeroCta(): HTMLElement | null {
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(MORFI_HERO_PRODUCT_CTA_SELECTOR));
  return candidates.find(el => el.getClientRects().length > 0) ?? null;
}
export function useLandingHeaderProductReveal(header) {
  const $ = _c(6);
  const [revealed, setRevealed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  let t0;
  let t1;
  if ($[0] !== header) {
    t0 = () => {
      const el = header;
      if (el === null) {
        return;
      }
      const syncHeight = () => {
        setHeaderHeight(el.getBoundingClientRect().height);
      };
      const ro = new ResizeObserver(syncHeight);
      ro.observe(el);
      syncHeight();
      return () => {
        ro.disconnect();
      };
    };
    t1 = [header];
    $[0] = header;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== headerHeight) {
    t2 = () => {
      let rafId = null;
      let framesLeft = HERO_CTA_RESOLVE_MAX_FRAMES;
      let heroCta = null;
      const syncReveal = () => {
        if (heroCta === null) {
          return;
        }
        const {
          bottom,
          top
        } = heroCta.getBoundingClientRect();
        const heroCtaIsVisible = bottom > headerHeight && top < window.innerHeight;
        setRevealed(!heroCtaIsVisible);
      };
      const attach = () => {
        heroCta = resolveLaidOutHeroCta();
        if (heroCta === null) {
          if (framesLeft > 0) {
            framesLeft = framesLeft - 1;
            framesLeft;
            rafId = requestAnimationFrame(attach);
          }
          return;
        }
        syncReveal();
      };
      window.addEventListener("scroll", syncReveal, {
        passive: true
      });
      window.addEventListener("resize", syncReveal);
      attach();
      return () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        window.removeEventListener("scroll", syncReveal);
        window.removeEventListener("resize", syncReveal);
      };
    };
    t3 = [headerHeight];
    $[3] = headerHeight;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  return revealed;
}